import { neon } from '@neondatabase/serverless';
import * as dotenv from 'dotenv';
import { getEnvironmentValue, ENVIRONMENT_VALUES, isBuildTime, getJavascriptEnvironment } from '@shared/utils';
import type { Result, AppError } from '@shared/utils';
import { err, tryCatch } from '@shared/utils';

/**
 * @ignore
 */
function createDatabaseConnection(): Result<any, AppError> {
  // Check if we're in a browser environment - database should never run on client
  // This prevents database connections from being bundled into browser code during Next.js compilation
  const envResult = getJavascriptEnvironment();
  const jsEnv = envResult.match({
    ok: (env) => env,
    err: () => 'node' as const
  });

  if (jsEnv === 'browser') {
    return err({
      errorCode: 'DATABASE_NOT_AVAILABLE_CLIENT_SIDE',
      errorMessage: 'Database connections are not available on the client side. Use API routes for data access.'
    });
  }

  // Check if we're in Next.js compilation phase (not general build time)
  // This only blocks during Next.js bundling, not during seed scripts or other Node CLI tools
  const buildTimeResult = isBuildTime();
  const isBuild = buildTimeResult.match({
    ok: (value) => value,
    err: () => false
  });

  // Only check for Next.js specific build phase, not general production builds
  const nextPhaseResult = getEnvironmentValue(ENVIRONMENT_VALUES.BUILD.NEXT_PHASE);
  const isNextJsBuild = nextPhaseResult.match({
    ok: (phase) => phase === 'phase-production-build',
    err: () => false
  });

  if (isNextJsBuild) {
    return err({
      errorCode: 'DATABASE_SKIPPED_NEXTJS_BUILD',
      errorMessage: 'Database connection skipped during Next.js compilation. Use API routes for data access.'
    });
  }

  // Load dotenv if needed
  const overrideResult = getEnvironmentValue(ENVIRONMENT_VALUES.DATABASE.OVERRIDE_POSTGRES_URL);
  const postgresUrlResult = getEnvironmentValue(ENVIRONMENT_VALUES.DATABASE.POSTGRES_URL);
  const localPostgresUrlResult = getEnvironmentValue(ENVIRONMENT_VALUES.DATABASE.LOCAL_POSTGRES_URL);

  const shouldLoadDotenv = overrideResult.match({
    ok: (override) => override === 'TRUE',
    err: () => false
  }) || postgresUrlResult.match({
    ok: () => false,
    err: () => true
  }) || localPostgresUrlResult.match({
    ok: () => false,
    err: () => true
  });

  if (shouldLoadDotenv) {
    dotenv.config({ path: '.env.local' });
  }

  // Get connection string with proper Result handling
  const connectionStringResult: Result<string, AppError> = overrideResult.match({
    ok: (override) => {
      if (override === 'TRUE') {
        return getEnvironmentValue(ENVIRONMENT_VALUES.DATABASE.LOCAL_POSTGRES_URL, true);
      }
      return getEnvironmentValue(ENVIRONMENT_VALUES.DATABASE.POSTGRES_URL, true);
    },
    err: () => getEnvironmentValue(ENVIRONMENT_VALUES.DATABASE.POSTGRES_URL, true)
  });

  // First check if we have a valid connection string
  if (connectionStringResult.isErr) {
    return err(connectionStringResult.getFail());
  }

  const connectionString = connectionStringResult.getSuccess();

  return tryCatch(() => neon(connectionString))
    .mapErr((error) => ({
      errorCode: 'DATABASE_CONNECTION_FAILED',
      errorMessage: `Failed to create database connection: ${error instanceof Error ? error.message : String(error)}`
    }));
}

// Create the database connection
const sqlResult = createDatabaseConnection();

/**
 * @ignore
 * @internal
 */
export const sql = sqlResult;
